import { ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MetricasUsuario } from '@/lib/usuarioExport';
import { veNotificacoesDeTodos } from '@/lib/relatorioUsoColunas';
import { useAuth } from '@/contexts/AuthContext';
import { Periodo, PeriodoPreset, PERIODO_PRESETS } from '@/lib/periodoFiltro';
import type { DiasTrabalhadosResposta } from '@/services/api';
import {
  SINAIS_FILTRO, SinalFiltro, ResumoDiasTrabalhados,
  diasPorSemana, inicioDaSemana, somarDias,
} from '@/lib/diasTrabalhados';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelectCombobox, MultiSelectItem } from '@/components/ui/multi-select-combobox';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { capitalizar, haQuantoTempo } from './graficoPadrao';
import { AjudaSinais } from './AjudaSinais';

// Mesmas variáveis de atividade usadas no "Baixar resumo" (buildRelatorioUsoWorkbook) — o usuário
// escolhe qual comparar no gráfico, uma de cada vez, em vez de amontoar todas juntas.
const VARIAVEIS = [
  { key: 'projetos', label: 'Projetos' },
  { key: 'okrs', label: 'OKRs' },
  { key: 'calendario', label: 'Calendário' },
  { key: 'cronogramas', label: 'Anexos em projetos' },
  { key: 'fluxogramas', label: 'Fluxogramas' },
  { key: 'organogramas', label: 'Organogramas' },
] as const;

type VariavelKey = (typeof VARIAVEIS)[number]['key'];

const CORES_NIVEL_BADGE: Record<string, string> = {
  Alto: 'bg-emerald-100 text-emerald-700',
  Médio: 'bg-amber-100 text-amber-700',
  Baixo: 'bg-rose-100 text-rose-700',
};

const CORES_STATUS_BADGE: Record<string, string> = {
  Ativo: 'bg-emerald-100 text-emerald-700',
  'Em acompanhamento': 'bg-amber-100 text-amber-700',
  Inativo: 'bg-rose-100 text-rose-700',
  'Nunca acessou': 'bg-muted text-muted-foreground',
};

const formatarDia = (ymd: string | null | undefined) =>
  ymd ? new Date(`${ymd}T12:00:00`).toLocaleDateString('pt-BR') : 'nenhum';

const formatarDataHora = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;

const TODOS = '__todos__';

/** Barra única de filtros da Tabela por usuário e da Por semana. Empresa, departamento e pessoas
 *  filtram as pessoas já carregadas (sem nova busca); o sinal muda a contagem de dias trabalhados. */
function BarraFiltros({
  visao, empresas, departamentos, pessoas, empresa, onEmpresa, departamento, onDepartamento,
  selecionadas, onSelecionadas, sinal, onSinal, soSinalizados, onSoSinalizados,
  periodoPreset, onPeriodoChange,
}: {
  visao: 'usuario' | 'semana';
  empresas: string[];
  departamentos: string[];
  pessoas: MultiSelectItem[];
  empresa: string;
  onEmpresa: (v: string) => void;
  departamento: string;
  onDepartamento: (v: string) => void;
  selecionadas: MultiSelectItem[];
  onSelecionadas: (v: MultiSelectItem[]) => void;
  sinal: SinalFiltro;
  onSinal: (s: SinalFiltro) => void;
  soSinalizados: boolean;
  onSoSinalizados: (v: boolean) => void;
  periodoPreset: PeriodoPreset;
  onPeriodoChange?: (p: PeriodoPreset) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-card px-3 py-2.5 text-card-foreground shadow-sm">
      <Select value={empresa} onValueChange={onEmpresa}>
        <SelectTrigger className="h-8 w-[190px] text-xs" aria-label="Empresa">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TODOS}>Todas as empresas</SelectItem>
          {empresas.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={departamento} onValueChange={onDepartamento} disabled={departamentos.length === 0}>
        <SelectTrigger className="h-8 w-[190px] text-xs" aria-label="Departamento">
          <SelectValue placeholder="Sem departamentos" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TODOS}>{departamentos.length ? 'Todos os departamentos' : 'Sem departamentos'}</SelectItem>
          {departamentos.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="w-[190px]">
        <MultiSelectCombobox
          items={pessoas}
          value={selecionadas}
          onChange={onSelecionadas}
          placeholder="Pessoas..."
          emptyLabel="Nenhuma pessoa encontrada."
          allowCreate={false}
          triggerClassName="h-8 text-xs"
        />
      </div>
      {visao === 'usuario' && onPeriodoChange && (
        <Select value={periodoPreset} onValueChange={(v) => onPeriodoChange(v as PeriodoPreset)}>
          <SelectTrigger className="h-8 w-[190px] text-xs" aria-label="Período">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODO_PRESETS.map((p) => (
              <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {visao === 'semana' && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="mr-1 flex items-center gap-1 text-xs text-muted-foreground">Contar por <AjudaSinais /></span>
          {SINAIS_FILTRO.map((op) => (
            <Button
              key={op.key}
              variant={sinal === op.key ? 'default' : 'outline'}
              size="sm"
              className="h-7 px-2 text-xs"
              title={op.descricao}
              onClick={() => onSinal(op.key)}
            >
              {op.label}
            </Button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Checkbox id="so-sinalizados" checked={soSinalizados} onCheckedChange={(v) => onSoSinalizados(v === true)} />
        <Label htmlFor="so-sinalizados" className="flex items-center gap-1 text-xs">
          <AlertTriangle className="h-3 w-3 text-amber-600" /> Só sinalizados
        </Label>
      </div>
    </div>
  );
}

// ============================================
// Aba "Tabela por usuário"
// ============================================

interface TabelaDesempenhoProps {
  /** Aba "Tabela por usuário" (uma linha por pessoa, todas as colunas) ou aba "Por semana". */
  visao: 'usuario' | 'semana';
  metricas: MetricasUsuario[];
  mostrarColunaEmpresa: boolean;
  periodoPreset: PeriodoPreset;
  periodo: Periodo;
  dias: Map<string, ResumoDiasTrabalhados>;
  diasTrabalhados: DiasTrabalhadosResposta | null;
  sinal: SinalFiltro;
  onSinalChange: (s: SinalFiltro) => void;
  soSinalizados: boolean;
  onSoSinalizadosChange: (v: boolean) => void;
  /** Mesmas métricas no período anterior de mesmo tamanho (id → métricas); null = sem comparação. */
  anteriores: Map<string, MetricasUsuario> | null;
  diasAnteriores: Map<string, ResumoDiasTrabalhados> | null;
  /** Só usado na visão "usuario" — a de "semana" mantém o seletor de período no cabeçalho da página. */
  onPeriodoChange?: (p: PeriodoPreset) => void;
}

/** Variação em relação ao período anterior (↑ verde, ↓ vermelho, nada quando igual). */
function Variacao({ atual, anterior }: { atual: number; anterior?: number }) {
  if (anterior === undefined || atual === anterior) return null;
  const sobe = atual > anterior;
  return (
    <span className={`ml-1 text-[11px] font-medium ${sobe ? 'text-emerald-600' : 'text-rose-600'}`} title={`Período anterior: ${anterior}`}>
      {sobe ? '↑' : '↓'}{Math.abs(atual - anterior)}
    </span>
  );
}

export function TabelaDesempenho({
  visao, metricas, mostrarColunaEmpresa, periodoPreset, periodo, dias, diasTrabalhados, sinal, onSinalChange,
  soSinalizados, onSoSinalizadosChange, anteriores, diasAnteriores, onPeriodoChange,
}: TabelaDesempenhoProps) {
  const navigate = useNavigate();
  const [variavel, setVariavel] = useState<VariavelKey>('projetos');

  const sinalAtual = SINAIS_FILTRO.find((s) => s.key === sinal) ?? SINAIS_FILTRO[0];
  // Coluna Departamento só aparece se alguém tiver departamento (senão é uma coluna inteira de "-").
  const mostrarDepartamento = useMemo(() => metricas.some((m) => m.departamento), [metricas]);
  // Admin não vê notificações de outras pessoas (só a própria linha teria número): a coluna é do superadmin.
  const { user } = useAuth();
  const mostrarNotificacoes = veNotificacoesDeTodos(user?.tipo);

  const [empresa, setEmpresa] = useState(TODOS);
  const [departamento, setDepartamento] = useState(TODOS);
  const [selecionadas, setSelecionadas] = useState<MultiSelectItem[]>([]);

  const opcoes = useMemo(() => {
    const empresas = new Set<string>();
    const departamentos = new Set<string>();
    metricas.forEach((m) => {
      (m.empresas || '').split(', ').filter(Boolean).forEach((e) => empresas.add(e));
      if (m.departamento) departamentos.add(m.departamento);
    });
    const ordenar = (c: Set<string>) => [...c].sort((a, b) => a.localeCompare(b));
    return {
      empresas: ordenar(empresas),
      departamentos: ordenar(departamentos),
      pessoas: [...metricas].sort((a, b) => a.nome.localeCompare(b.nome)).map((m) => ({ id: m.id, nome: m.nome })),
    };
  }, [metricas]);

  const linhas = useMemo(() => {
    const ids = new Set(selecionadas.map((p) => p.id));
    const base = metricas.filter((m) =>
      (!soSinalizados || dias.get(m.id)?.sinalizado)
      && (empresa === TODOS || (m.empresas || '').split(', ').includes(empresa))
      && (departamento === TODOS || m.departamento === departamento)
      && (ids.size === 0 || ids.has(m.id)));
    return [...base].sort((a, b) => b[variavel] - a[variavel] || a.nome.localeCompare(b.nome));
  }, [metricas, soSinalizados, dias, variavel, empresa, departamento, selecionadas]);

  const abrirModulo = (usuarioId: string, modulo: string) => {
    const qs = new URLSearchParams({ modulo });
    if (periodoPreset !== 'tudo') qs.set('periodo', periodoPreset);
    navigate(`/usuarios/${usuarioId}?${qs.toString()}`);
  };

  // Pessoa × semana: semanas do período (sem período, as últimas 12).
  const intervaloSemanas = useMemo(() => {
    if (!diasTrabalhados) return null;
    const fim = periodo.ate && periodo.ate < diasTrabalhados.hoje ? periodo.ate : diasTrabalhados.hoje;
    const inicio = periodo.de ?? inicioDaSemana(somarDias(fim, -7 * 11));
    return { inicio, fim, semanas: diasPorSemana(undefined, inicio, fim, sinal).map((s) => s.inicio) };
  }, [diasTrabalhados, periodo, sinal]);

  return (
    <div className="space-y-3">
      <BarraFiltros
        visao={visao}
        empresas={opcoes.empresas}
        departamentos={opcoes.departamentos}
        pessoas={opcoes.pessoas}
        empresa={empresa}
        onEmpresa={setEmpresa}
        departamento={departamento}
        onDepartamento={setDepartamento}
        selecionadas={selecionadas}
        onSelecionadas={setSelecionadas}
        sinal={sinal}
        onSinal={onSinalChange}
        soSinalizados={soSinalizados}
        onSoSinalizados={onSoSinalizadosChange}
        periodoPreset={periodoPreset}
        onPeriodoChange={onPeriodoChange}
      />
      {anteriores && visao === 'usuario' && (
        <p className="text-xs text-muted-foreground">↑↓ = diferença para o período anterior de mesmo tamanho.</p>
      )}

      {linhas.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Nenhum usuário para mostrar.</p>
      ) : visao === 'usuario' ? (
        <div className="max-w-full overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-10 min-w-[200px] bg-card">Usuário</TableHead>
                {mostrarDepartamento && <TableHead>Departamento</TableHead>}
                {mostrarColunaEmpresa && <TableHead>Empresas</TableHead>}
                {VARIAVEIS.map((v) => (
                  <TableHead key={v.key} className="whitespace-nowrap text-center">
                    <button
                      type="button"
                      className={v.key === variavel ? 'font-semibold text-foreground' : 'hover:text-foreground'}
                      title="Ordenar por este módulo"
                      onClick={() => setVariavel(v.key)}
                    >
                      {v.label}{v.key === variavel ? ' ↓' : ''}
                    </button>
                  </TableHead>
                ))}
                <TableHead className="whitespace-nowrap text-center" title={sinalAtual.descricao}>
                  Dias trab.{sinal !== 'todos' ? ` (${sinalAtual.label})` : ''}
                </TableHead>
                <TableHead className="whitespace-nowrap text-center" title="Dias úteis (seg–sex, sem feriados nacionais) sem o sinal escolhido">
                  Úteis sem sinal
                </TableHead>
                <TableHead className="whitespace-nowrap">Último acesso</TableHead>
                <TableHead className="min-w-[260px]">Última ação</TableHead>
                <TableHead className="text-center">Produtividade</TableHead>
                {mostrarNotificacoes && <TableHead className="whitespace-nowrap text-center">Notif. não lidas</TableHead>}
                <TableHead>Status de acesso</TableHead>
                <TableHead>Nível</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.map((m) => {
                const d = dias.get(m.id);
                const u = diasTrabalhados?.usuarios[m.id];
                const ant = anteriores?.get(m.id);
                return (
                  <TableRow key={m.id}>
                    <TableCell className="sticky left-0 z-10 bg-card font-medium">
                      <div>{m.nome}</div>
                      {d?.sinalizado && (
                        <Badge
                          variant="secondary"
                          className="mt-1 gap-1 whitespace-nowrap bg-amber-100 text-[11px] text-amber-800"
                          title={`Último registro: ${formatarDia(d.ultimoRegistro)}`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          {d.diasSemRegistro} dias sem registro
                        </Badge>
                      )}
                    </TableCell>
                    {mostrarDepartamento && <TableCell className="text-sm text-muted-foreground">{m.departamento || '-'}</TableCell>}
                    {mostrarColunaEmpresa && (
                      <TableCell className="text-sm text-muted-foreground">
                        {/* Uma linha só (o nome completo no hover) — várias empresas quebravam a linha em 3 */}
                        <div className="max-w-[180px] truncate" title={m.empresas || undefined}>
                          {m.empresas ? (m.empresas.split(', ').length > 1 ? `${m.empresas.split(', ')[0]} +${m.empresas.split(', ').length - 1}` : m.empresas) : '-'}
                        </div>
                      </TableCell>
                    )}
                    {VARIAVEIS.map((v) => (
                      <TableCell key={v.key} className="whitespace-nowrap text-center">
                        {m[v.key] > 0 ? (
                          <button
                            type="button"
                            className="rounded px-1.5 py-0.5 font-medium text-primary underline-offset-2 hover:underline"
                            title={`Ver ${v.label} de ${m.nome}`}
                            onClick={() => abrirModulo(m.id, v.key)}
                          >
                            {m[v.key]}
                          </button>
                        ) : (
                          <span className="px-1.5 text-muted-foreground/60">0</span>
                        )}
                        <Variacao atual={m[v.key]} anterior={ant?.[v.key]} />
                      </TableCell>
                    ))}
                    <TableCell className="text-center">
                      {d ? (
                        <>
                          <div className="whitespace-nowrap font-medium">
                            {d.diasTrabalhados}
                            <Variacao atual={d.diasTrabalhados} anterior={diasAnteriores?.get(m.id)?.diasTrabalhados} />
                          </div>
                          {sinal === 'todos' && (
                            <div className="whitespace-nowrap text-[11px] text-muted-foreground">
                              App {d.porSinal.acesso} · Agenda {d.porSinal.agenda} · Ação {d.porSinal.acao}
                            </div>
                          )}
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-center text-sm">{d?.diasUteisSemSinal ?? '—'}</TableCell>
                    <TableCell className="text-sm">
                      {u?.ultimo_acesso ? (
                        <>
                          <div className="whitespace-nowrap">{formatarDataHora(u.ultimo_acesso)}</div>
                          <div className="text-[11px] text-muted-foreground">{haQuantoTempo(m.dias)}</div>
                        </>
                      ) : <span className="text-muted-foreground">Nunca acessou</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {u?.ultima_acao ? (
                        <button
                          type="button"
                          className="max-w-[300px] text-left hover:underline"
                          title={`${capitalizar(u.ultima_acao.descricao)} — ver linha do tempo`}
                          onClick={() => abrirModulo(m.id, 'linha_do_tempo')}
                        >
                          <div className="line-clamp-2 break-words">{capitalizar(u.ultima_acao.descricao)}</div>
                          <div className="whitespace-nowrap text-[11px] text-muted-foreground">
                            {capitalizar(u.ultima_acao.modulo)} · {formatarDataHora(u.ultima_acao.ts)}
                          </div>
                        </button>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-center">{m.produtividade.toFixed(2)}</TableCell>
                    {mostrarNotificacoes && (
                      <TableCell className="text-center">
                        {m.notificacoesNaoLidas ?? (
                          <span className="text-muted-foreground" title="Só o superadmin e a própria pessoa veem as notificações">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant="secondary" className={`whitespace-nowrap ${CORES_STATUS_BADGE[m.statusAcessoLabel] || ''}`}>
                        {m.statusAcessoLabel}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`whitespace-nowrap ${CORES_NIVEL_BADGE[m.nivelUtilizacao] || ''}`}>
                        {m.nivelUtilizacao}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="max-w-full overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-10 min-w-[200px] bg-card">Usuário</TableHead>
                {intervaloSemanas?.semanas.map((s) => (
                  <TableHead key={s} className="whitespace-nowrap text-center" title={`Semana de ${formatarDia(s)}`}>
                    {formatarDia(s).slice(0, 5)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.map((m) => {
                const porSemana = diasTrabalhados && intervaloSemanas
                  ? diasPorSemana(diasTrabalhados.usuarios[m.id], intervaloSemanas.inicio, intervaloSemanas.fim, sinal)
                  : [];
                return (
                  <TableRow key={m.id}>
                    <TableCell className="sticky left-0 z-10 bg-card font-medium">{m.nome}</TableCell>
                    {porSemana.map((s) => {
                      const cor = s.comSinal === 0
                        ? (s.uteis > 0 ? 'bg-rose-100 text-rose-800' : 'text-muted-foreground')
                        : s.comSinal >= s.uteis ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-50 text-amber-900';
                      return (
                        <TableCell key={s.inicio} className="p-1 text-center">
                          <span
                            className={`inline-block min-w-[44px] rounded px-1.5 py-0.5 text-xs ${cor}`}
                            title={`${s.comSinal} dia(s) com sinal · ${s.uteis} dia(s) úteis`}
                          >
                            {s.comSinal}/{s.uteis}
                          </span>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            Cada célula: dias com sinal / dias úteis da semana (seg–sex, sem feriados nacionais). Verde = todos os dias úteis
            com sinal; vermelho = nenhum.
          </p>
        </div>
      )}
    </div>
  );
}
